import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Hash, Image as ImageIcon, Microphone, FileArrowUp, CheckCircle, Circle } from "@phosphor-icons/react";
import { api } from "../lib/api";

const STATUS_STEPS = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"];
const STEP_LABEL = { OPEN: "Open", IN_PROGRESS: "In progress", RESOLVED: "Resolved", CLOSED: "Closed" };

function elapsed(a, b) {
  const ms = new Date(b) - new Date(a);
  if (ms < 0 || isNaN(ms)) return "";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hr`;
  return `${Math.round(hrs / 24)} days`;
}

const STATUS_TINT = {
  OPEN: "border-neon-pink/30 text-neon-pink bg-neon-pink/10",
  IN_PROGRESS: "border-neon-amber/30 text-neon-amber bg-neon-amber/10",
  RESOLVED: "border-neon-lime/30 text-neon-lime bg-neon-lime/10",
  CLOSED: "border-white/15 text-white/60 bg-white/5",
};

function AttachmentView({ cid, att }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const { data } = await api.get(`/api/complaints/${cid}/attachments/${encodeURIComponent(att.name)}`, { responseType: "blob" });
        url = URL.createObjectURL(data);
        setSrc(url);
      } catch { /* ignore */ }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [cid, att.name]);

  if (att.kind === "IMAGE") {
    return src
      ? <img src={src} alt={att.name} className="max-h-72 w-full rounded-xl object-cover" />
      : <div className="flex h-40 items-center justify-center rounded-xl bg-white/[0.04] text-white/40"><ImageIcon size={24} weight="duotone" /></div>;
  }
  if (att.kind === "AUDIO") {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-white/[0.04] px-3 py-2">
        <Microphone size={16} weight="fill" className="text-neon-pink" />
        {src ? <audio controls src={src} className="h-8 flex-1" /> : <span className="text-[12px] text-white/55">Voice note</span>}
      </div>
    );
  }
  return (
    <a href={src || undefined} download={att.name} className="flex items-center gap-2 rounded-xl bg-white/[0.04] px-3 py-2 text-white/80">
      <FileArrowUp size={16} weight="fill" className="text-neon-lime" />
      <span className="min-w-0 flex-1 truncate text-[12px] font-semibold">{att.name}</span>
    </a>
  );
}

export default function ComplaintDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/api/complaints/${id}`).then(({ data }) => setC(data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="font-sans"><BackBar onBack={() => navigate(-1)} /><div className="card mt-3 p-8 text-center text-white/55">Ticket not found.</div></div>;
  if (!c) return <div className="font-sans"><BackBar onBack={() => navigate(-1)} /><div className="card mt-3 p-8 text-center text-white/55">Loading…</div></div>;

  const attachments = c.attachments || [];

  return (
    <div className="space-y-4 font-sans" data-testid="complaint-detail">
      <BackBar onBack={() => navigate(-1)} />

      <div className="card p-5">
        {c.ticketId && (
          <span className="inline-flex items-center gap-1 rounded-md border border-neon-cyan/30 bg-neon-cyan/10 px-1.5 py-0.5 font-mono text-[10px] font-bold text-neon-cyan">
            <Hash size={9} weight="bold" />{c.ticketId.replace("TKT-", "")}
          </span>
        )}
        <h1 className="mt-1 font-display text-2xl font-bold leading-snug text-white">{c.title}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className={`chip ${STATUS_TINT[c.status] || ""}`}>{(c.status || "").replace("_", " ")}</span>
          <span className="chip">{c.category}</span>
          {c.flatId && <span className="chip">Flat {c.flatId}</span>}
          <span className="text-[10px] uppercase tracking-wider text-white/40">{new Date(c.createdAt).toLocaleString()}</span>
        </div>
        <p className="mt-4 whitespace-pre-wrap text-[14px] leading-relaxed text-white/75">{c.description}</p>
      </div>

      {/* Status workflow */}
      <div className="card p-5">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Progress</p>
        {(() => {
          const cur = Math.max(0, STATUS_STEPS.indexOf(c.status));
          const hist = c.history || []; // future-proof: backend per-state log when available
          return (
            <div className="space-y-0">
              {STATUS_STEPS.map((s, i) => {
                const reached = i <= cur;
                const isCurrent = i === cur;
                // Real timestamps we know: OPEN = createdAt; current state = updatedAt.
                const ts = hist.find((h) => h.status === s)?.at
                  || (s === "OPEN" ? c.createdAt : isCurrent ? c.updatedAt : null);
                return (
                  <div key={s} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      {reached ? <CheckCircle size={20} weight="fill" className={isCurrent ? "text-neon-cyan" : "text-neon-lime"} /> : <Circle size={20} weight="bold" className="text-white/25" />}
                      {i < STATUS_STEPS.length - 1 && <span className={`my-0.5 w-0.5 flex-1 ${i < cur ? "bg-neon-lime/40" : "bg-white/10"}`} style={{ minHeight: 18 }} />}
                    </div>
                    <div className="pb-3">
                      <p className={`text-[14px] font-semibold ${reached ? "text-white" : "text-white/40"}`}>{STEP_LABEL[s]}</p>
                      {ts ? <p className="text-[11px] text-white/45">{new Date(ts).toLocaleString()}</p> : reached ? null : <p className="text-[11px] text-white/30">Pending</p>}
                    </div>
                  </div>
                );
              })}
              <p className="mt-1 text-[11px] text-white/40">Updated by the society office · {elapsed(c.createdAt, c.updatedAt)} since raised</p>
            </div>
          );
        })()}
      </div>

      {attachments.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Attachments</p>
          <div className="space-y-2">
            {attachments.map((a) => (
              <div key={a.id || a.name} className="card p-2"><AttachmentView cid={c.id} att={a} /></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BackBar({ onBack }) {
  return (
    <button onClick={onBack} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/70 hover:text-white">
      <ArrowLeft size={16} weight="bold" /> Back to tickets
    </button>
  );
}
